import { createOpenAI } from '@ai-sdk/openai';
import { generateText, Output, stepCountIs, tool, type ModelMessage, type UserContent } from 'ai';
import { z } from 'zod/v4';

import { confidenceLevels, mealTypes, nutritionSourceKinds } from '@calorie-tracker/nutrition';

import { defaultNutritionModel } from './models.ts';

import type { CanonicalNutritionEntryDraft } from '@calorie-tracker/nutrition';

export const nutritionLogAgentToolNames = [
  'getLoggedMealHistory',
  'searchFoodLibrary',
  'searchUsdaFoods',
  'getUsdaFoodDetails',
  'searchWebNutrition',
  'calculateNutritionForQuantity',
  'calculateExpression',
  'normalizeDraftEntry',
  'createDraftSuggestion',
] as const;

export type NutritionLogAgentToolName = (typeof nutritionLogAgentToolNames)[number];

export const nutritionLogAgentSystemPrompt = [
  'You are the food logging agent for a calorie tracker.',
  'Create reviewable draft entries only. Never save final food entries.',
  'Images are provided directly in the user message; inspect them yourself and use tools only for lookup, math, validation, and draft persistence.',
  'When the user refers to a prior meal such as "same as yesterday breakfast", use getLoggedMealHistory before using library or nutrition lookup tools.',
  'Prefer the user food library for repeated foods. Prefer USDA for generic foods and ingredients.',
  'For compound foods such as sandwiches, wraps, burgers, bowls, salads, and burritos, break the food into likely components when practical, such as bread, meat, cheese, sauces, vegetables, and sides.',
  'Use component-level lookup and calculation for compound foods when it will make the draft easier for the user to verify or easier to validate against USDA or web nutrition sources.',
  'If component details are uncertain, estimate conservatively, preserve the compound food name in originalInput, and ask one short clarification question instead of over-specifying when confidence is low.',
  'Use web nutrition search for branded, packaged, restaurant, menu, or ambiguous foods that need cited current evidence.',
  'Use calculateNutritionForQuantity for nutrition scaling and unit-aware quantity math.',
  'Use calculateExpression only as a fallback for simple arithmetic, never to invent nutrition facts or unsafe unit conversions.',
  'Before proposing draft entries, sanity-check the calories and macros against the stated quantity and common nutrition facts; fix obvious impossibilities before creating the suggestion.',
  'For example, do not propose one teaspoon of oil as 10 calories when one tablespoon of oil is about 120 calories, making one teaspoon about 40 calories.',
  'If a number fails this common-sense check and you cannot resolve it with lookup or calculation tools, lower confidence or ask one short clarification question instead of presenting it as reliable.',
  'When you have enough information to propose entries, every draft entry must be passed through normalizeDraftEntry before createDraftSuggestion.',
  'Call createDraftSuggestion only when you have normalized draft entries ready for user review.',
  'If you need more information, do not create a draft; return one short clarification question instead.',
  'Keep clarification questions short and ask at most one when confidence is low.',
  'Preserve the original user wording in originalInput.',
  'For consumedAt, use the local submission timestamp string with its offset unless the user clearly states a different meal time; do not substitute the UTC reference timestamp for normal logs.',
].join(' ');

export interface NutritionLogAgentImageInput {
  data: ArrayBuffer | Uint8Array | string;
  filename?: string;
  mediaType: string;
  purpose: 'food-label' | 'meal-photo';
}

export interface NutritionLogAgentRequest {
  images?: NutritionLogAgentImageInput[];
  submittedAt: string;
  text?: string;
}

export interface NutritionLogAgentConversationMessage {
  images?: NutritionLogAgentImageInput[];
  role: 'assistant' | 'user';
  submittedAt?: string;
  text: string;
}

export interface NutritionLogAgentRuntime {
  nutritionModel?: string;
  openAiApiKey?: string;
}

export interface NutritionLogAgentResult {
  clarificationQuestion: string | null;
  draftEntries: CanonicalNutritionEntryDraft[];
  message: string;
  rawResponse: Record<string, unknown> | null;
  suggestionId: string | null;
}

const nutritionLogAgentOutputSchema = z.object({
  clarificationQuestion: z.string().trim().min(1).max(280).nullable(),
  message: z.string().trim().min(1).max(500),
  suggestionId: z.string().trim().min(1).nullable(),
});

const quantitySchema = z.object({
  amount: z.number().finite().positive(),
  unit: z.string().trim().min(1),
});

const macrosSchema = z.object({
  calories: z.number().finite().nonnegative(),
  proteinGrams: z.number().finite().nonnegative(),
  carbsGrams: z.number().finite().nonnegative(),
  fatGrams: z.number().finite().nonnegative(),
  fiberGrams: z.number().finite().nonnegative().optional(),
  sugarGrams: z.number().finite().nonnegative().optional(),
  sodiumMilligrams: z.number().finite().nonnegative().optional(),
});

const draftInputSchema = z.object({
  mealType: z.enum(mealTypes),
  originalInput: z.string().trim().min(1),
  normalizedName: z.string().trim().min(1),
  inputQuantity: quantitySchema,
  canonicalQuantity: quantitySchema,
  macros: macrosSchema,
  source: z.enum(nutritionSourceKinds),
  confidence: z.enum(confidenceLevels),
  consumedAt: z.string().trim().min(1),
});

const draftInputArraySchema = z.array(draftInputSchema);

const draftOutputSchema = draftInputSchema.extend({
  requiresReview: z.boolean(),
});

const toolUnavailableOutputSchema = z.object({
  available: z.literal(false),
  reason: z.string(),
});

const searchFoodLibraryOutputSchema = z.union([
  toolUnavailableOutputSchema,
  z.object({
    available: z.literal(true),
    foods: z.array(
      z.object({
        confidence: z.enum(confidenceLevels),
        defaultQuantity: quantitySchema,
        foodId: z.string(),
        macros: macrosSchema,
        name: z.string(),
        source: z.string(),
      }),
    ),
    meals: z.array(
      z.object({
        entries: z.array(
          z.object({
            macros: macrosSchema,
            name: z.string(),
            quantity: quantitySchema,
          }),
        ),
        key: z.string(),
        logCount: z.number(),
        totals: macrosSchema,
      }),
    ),
  }),
]);

const loggedMealHistoryOutputSchema = z.union([
  toolUnavailableOutputSchema,
  z.object({
    available: z.literal(true),
    meals: z.array(
      z.object({
        consumedAt: z.string(),
        consumedOn: z.string(),
        copiedDraftEntries: z.array(draftInputSchema),
        entryCount: z.number(),
        mealId: z.string(),
        mealType: z.enum(mealTypes),
      }),
    ),
  }),
]);

const searchUsdaFoodsOutputSchema = z.union([
  toolUnavailableOutputSchema,
  z.object({
    available: z.literal(true),
    results: z.array(
      z.object({
        brandOwner: z.string().optional(),
        dataType: z.string().optional(),
        description: z.string().optional(),
        fdcId: z.number(),
      }),
    ),
  }),
]);

const getUsdaFoodDetailsOutputSchema = z.union([
  toolUnavailableOutputSchema,
  z.object({
    available: z.literal(true),
    dataType: z.string().optional(),
    description: z.string().optional(),
    fdcId: z.number(),
    nutrientsPer100Grams: macrosSchema.optional(),
    portions: z.array(
      z.object({
        amount: z.number().optional(),
        gramWeight: z.number().optional(),
        label: z.string(),
      }),
    ),
    resolvedDraft: draftInputSchema.optional(),
    warnings: z.array(z.string()),
  }),
]);

const searchWebNutritionOutputSchema = z.union([
  toolUnavailableOutputSchema,
  z.object({
    available: z.literal(true),
    draftEntries: z.array(draftInputSchema),
    rawResponse: z.unknown().nullable(),
  }),
]);

const calculateNutritionOutputSchema = z.object({
  canonicalQuantity: quantitySchema.optional(),
  confidence: z.enum(confidenceLevels),
  macros: macrosSchema.optional(),
  status: z.enum(['calculated', 'needs-clarification', 'unsupported']),
  warnings: z.array(z.string()),
});

const calculateExpressionOutputSchema = z.object({
  expression: z.string(),
  result: z.number(),
});

const normalizeDraftOutputSchema = z.object({
  draftEntry: draftOutputSchema,
  validationWarnings: z.array(z.string()),
});

const createDraftSuggestionOutputSchema = z.object({
  clarificationQuestion: z.string().nullable(),
  createdAt: z.string(),
  draftEntries: z.array(draftOutputSchema),
  message: z.string(),
  suggestionId: z.string(),
});

const nutritionCalculationServingFactsSchema = z.object({
  gramWeight: z.number().finite().positive().optional(),
  servingSizeText: z.string().trim().min(1).optional(),
  servingsPerContainer: z.number().finite().positive().optional(),
});

type MaybePromise<T> = Promise<T> | T;

export interface NutritionLogAgentToolImplementations {
  calculateExpression(input: {
    expression: string;
    reason: string;
  }): MaybePromise<z.infer<typeof calculateExpressionOutputSchema>>;
  calculateNutritionForQuantity(input: {
    knownMacros: z.infer<typeof macrosSchema>;
    knownQuantity: z.infer<typeof quantitySchema>;
    requestedQuantity: z.infer<typeof quantitySchema>;
    servingFacts?: z.infer<typeof nutritionCalculationServingFactsSchema>;
  }): MaybePromise<z.infer<typeof calculateNutritionOutputSchema>>;
  createDraftSuggestion(input: {
    clarificationQuestion: string | null;
    draftEntries: z.infer<typeof draftInputArraySchema>;
    message?: string;
  }): MaybePromise<z.infer<typeof createDraftSuggestionOutputSchema>>;
  getUsdaFoodDetails(input: {
    consumedAt: string;
    fdcId: number;
    mealType: string;
    normalizedName: string;
    originalInput: string;
    quantity: z.infer<typeof quantitySchema>;
  }): MaybePromise<z.infer<typeof getUsdaFoodDetailsOutputSchema>>;
  getLoggedMealHistory(input: {
    mealType?: string;
    relativeDay?: 'today' | 'yesterday';
    targetDate?: string;
  }): MaybePromise<z.infer<typeof loggedMealHistoryOutputSchema>>;
  normalizeDraftEntry(
    input: z.infer<typeof draftInputSchema>,
  ): MaybePromise<z.infer<typeof normalizeDraftOutputSchema>>;
  searchFoodLibrary(input: {
    query: string;
    limit?: number;
  }): MaybePromise<z.infer<typeof searchFoodLibraryOutputSchema>>;
  searchUsdaFoods(input: {
    query: string;
    maxResults?: number;
  }): MaybePromise<z.infer<typeof searchUsdaFoodsOutputSchema>>;
  searchWebNutrition(input: {
    consumedAt: string;
    mealType: string;
    normalizedName: string;
    originalInput: string;
    quantity: z.infer<typeof quantitySchema>;
  }): MaybePromise<z.infer<typeof searchWebNutritionOutputSchema>>;
}

export type NutritionLogAgentTools = ReturnType<typeof createNutritionLogAgentTools>;

function agentTool(definition: unknown): never {
  return tool(definition as never) as never;
}

export function createNutritionLogAgentTools(
  implementations: NutritionLogAgentToolImplementations,
) {
  return {
    getLoggedMealHistory: agentTool({
      description:
        'Fetch exact saved meals and copyable draft entries for date-specific references like yesterday breakfast, today lunch, or a named calendar date.',
      inputSchema: z.object({
        targetDate: z
          .string()
          .trim()
          .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.')
          .optional(),
        relativeDay: z.enum(['today', 'yesterday']).optional(),
        mealType: z.enum(mealTypes).optional(),
      }),
      outputSchema: loggedMealHistoryOutputSchema,
      execute: implementations.getLoggedMealHistory,
    }),
    searchFoodLibrary: agentTool({
      description: 'Search the user food library, saved recipes, and frequent prior meals.',
      inputSchema: z.object({
        query: z.string().trim().min(1).describe('Food, recipe, or meal text to search for.'),
        limit: z.number().int().positive().max(10).optional(),
      }),
      outputSchema: searchFoodLibraryOutputSchema,
      execute: implementations.searchFoodLibrary,
    }),
    searchUsdaFoods: agentTool({
      description: 'Search USDA FoodData Central for generic foods, ingredients, and common foods.',
      inputSchema: z.object({
        query: z.string().trim().min(1),
        maxResults: z.number().int().positive().max(10).optional(),
      }),
      outputSchema: searchUsdaFoodsOutputSchema,
      execute: implementations.searchUsdaFoods,
    }),
    getUsdaFoodDetails: agentTool({
      description:
        'Fetch USDA details for a selected fdcId and, when possible, resolve nutrition for the requested quantity.',
      inputSchema: z.object({
        consumedAt: z.string().trim().min(1),
        fdcId: z.number().int().positive(),
        mealType: z.enum(['breakfast', 'lunch', 'dinner', 'snack']),
        normalizedName: z.string().trim().min(1),
        originalInput: z.string().trim().min(1),
        quantity: quantitySchema,
      }),
      outputSchema: getUsdaFoodDetailsOutputSchema,
      execute: implementations.getUsdaFoodDetails,
    }),
    searchWebNutrition: agentTool({
      description:
        'Resolve branded, packaged, restaurant, menu, or ambiguous nutrition with cited web evidence.',
      inputSchema: z.object({
        consumedAt: z.string().trim().min(1),
        mealType: z.enum(['breakfast', 'lunch', 'dinner', 'snack']),
        normalizedName: z.string().trim().min(1),
        originalInput: z.string().trim().min(1),
        quantity: quantitySchema,
      }),
      outputSchema: searchWebNutritionOutputSchema,
      execute: implementations.searchWebNutrition,
    }),
    calculateNutritionForQuantity: agentTool({
      description:
        'Preferred deterministic nutrition math tool for scaling known nutrition to a consumed quantity.',
      inputSchema: z.object({
        knownMacros: macrosSchema,
        knownQuantity: quantitySchema,
        requestedQuantity: quantitySchema,
        servingFacts: nutritionCalculationServingFactsSchema.optional(),
      }),
      outputSchema: calculateNutritionOutputSchema,
      execute: implementations.calculateNutritionForQuantity,
    }),
    calculateExpression: agentTool({
      description:
        'Fallback-only arithmetic for simple numeric checks. Do not use for nutrition facts or unsafe unit conversion.',
      inputSchema: z.object({
        expression: z.string().trim().min(1).max(200),
        reason: z.string().trim().min(1).max(200),
      }),
      outputSchema: calculateExpressionOutputSchema,
      execute: implementations.calculateExpression,
    }),
    normalizeDraftEntry: agentTool({
      description:
        'Validate and normalize one proposed food item into the canonical draft-entry format.',
      inputSchema: draftInputSchema,
      outputSchema: normalizeDraftOutputSchema,
      execute: implementations.normalizeDraftEntry,
    }),
    createDraftSuggestion: agentTool({
      description:
        'Persist all normalized draft entries for user review. This does not create final food entries.',
      inputSchema: z.object({
        clarificationQuestion: z.string().trim().min(1).max(280).nullable(),
        draftEntries: draftInputArraySchema,
        message: z.string().trim().min(1).max(500).optional(),
      }),
      outputSchema: createDraftSuggestionOutputSchema,
      execute: implementations.createDraftSuggestion,
    }),
  };
}

function buildImageContextLine(image: NutritionLogAgentImageInput, index: number): string {
  const descriptor = image.purpose === 'food-label' ? 'food label' : 'meal photo';
  const fileName = image.filename?.trim();

  return fileName && fileName.length > 0
    ? `Image ${index + 1}: ${descriptor} named ${fileName}.`
    : `Image ${index + 1}: ${descriptor}.`;
}

function buildUserPromptText(input: NutritionLogAgentRequest): string {
  const sections = [
    `Submission timestamp (local browser time; prefer this for consumedAt): ${input.submittedAt}.`,
    `Same instant in UTC for storage/reference only: ${new Date(input.submittedAt).toISOString()}.`,
  ];
  const normalizedText = input.text?.trim();

  if (normalizedText && normalizedText.length > 0) {
    sections.push(`User food log text or transcript:\n${normalizedText}`);
  } else {
    sections.push('User food log text or transcript: none provided.');
  }

  if ((input.images?.length ?? 0) > 0) {
    sections.push(input.images!.map(buildImageContextLine).join('\n'));
  }

  return sections.join('\n\n');
}

function buildPromptContent(input: NutritionLogAgentRequest): UserContent {
  const content: UserContent = [
    {
      type: 'text',
      text: buildUserPromptText(input),
    },
  ];

  for (const image of input.images ?? []) {
    content.push({
      type: 'image',
      image: image.data instanceof ArrayBuffer ? new Uint8Array(image.data) : image.data,
      mediaType: image.mediaType,
    });
  }

  return content;
}

function buildConversationUserPromptText(
  message: NutritionLogAgentConversationMessage,
  fallbackSubmittedAt: string,
): string {
  const submittedAt = message.submittedAt ?? fallbackSubmittedAt;
  const request: NutritionLogAgentRequest = {
    submittedAt,
    ...(message.text.trim().length > 0 ? { text: message.text } : {}),
    ...(message.images ? { images: message.images } : {}),
  };

  return buildUserPromptText(request);
}

function buildConversationUserContent(
  message: NutritionLogAgentConversationMessage,
  fallbackSubmittedAt: string,
): UserContent {
  const content: UserContent = [
    {
      type: 'text',
      text: buildConversationUserPromptText(message, fallbackSubmittedAt),
    },
  ];

  for (const image of message.images ?? []) {
    content.push({
      type: 'image',
      image: image.data instanceof ArrayBuffer ? new Uint8Array(image.data) : image.data,
      mediaType: image.mediaType,
    });
  }

  return content;
}

function buildAgentMessages(input: {
  conversation?: NutritionLogAgentConversationMessage[];
  request: NutritionLogAgentRequest;
}): ModelMessage[] {
  if (!input.conversation || input.conversation.length === 0) {
    return [
      {
        role: 'user',
        content: buildPromptContent(input.request),
      },
    ];
  }

  return input.conversation.map((message) =>
    message.role === 'assistant'
      ? {
          role: 'assistant',
          content: message.text,
        }
      : {
          role: 'user',
          content: buildConversationUserContent(message, input.request.submittedAt),
        },
  );
}

type GenerateNutritionLogAgentImplementation = typeof generateText;

export async function runNutritionLogAgent(input: {
  conversation?: NutritionLogAgentConversationMessage[];
  generate?: GenerateNutritionLogAgentImplementation;
  request: NutritionLogAgentRequest;
  runtime?: NutritionLogAgentRuntime;
  toolImplementations: NutritionLogAgentToolImplementations;
}): Promise<NutritionLogAgentResult> {
  const apiKey = input.runtime?.openAiApiKey?.trim();

  if (!apiKey) {
    throw new Error('Nutrition log agent is not configured on the server.');
  }

  const openai = createOpenAI({
    apiKey,
  });
  const generate = input.generate ?? generateText;
  const result = await generate({
    model: openai(input.runtime?.nutritionModel?.trim() || defaultNutritionModel),
    system: nutritionLogAgentSystemPrompt,
    tools: createNutritionLogAgentTools(input.toolImplementations),
    output: Output.object({
      schema: nutritionLogAgentOutputSchema,
    }),
    stopWhen: stepCountIs(12),
    temperature: 0,
    messages: buildAgentMessages({
      ...(input.conversation ? { conversation: input.conversation } : {}),
      request: input.request,
    }),
  });
  const output = result.output as z.infer<typeof nutritionLogAgentOutputSchema>;

  return {
    clarificationQuestion: output.clarificationQuestion,
    draftEntries: [],
    message: output.message,
    rawResponse: {
      finishReason: result.finishReason,
      usage: result.usage,
      steps: result.steps.length,
    },
    suggestionId: output.suggestionId,
  };
}
